"use client";

import React, { useEffect, useState } from "react";
import { 
  Calendar, 
  User, 
  Clock, 
  Share2, 
  ArrowRight, 
  Tag,
  ChevronRight
} from "lucide-react";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";



function formatDate(dateString?: string) {
  if (!dateString) return "";
  return new Date(dateString).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}



function RelatedPostsConsumer({ currentPostId }: { currentPostId: string }) {
  const { posts, loading, error } = usePosts();

  
  const relatedPosts = posts ? posts.filter(p => p.id !== currentPostId).slice(0, 3) : [];

  if (loading) {
    return (
      <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-96 animate-pulse rounded-xl bg-muted/20" />
        ))}
      </div>
    );
  }

  if (error || relatedPosts.length === 0) return null;

  return (
    <div className="mt-24 border-t border-border pt-16">
      <div className="mb-10 flex items-center justify-between">
        <h3 className="text-2xl font-bold tracking-tight text-foreground md:text-3xl">
          Related Drops
        </h3>
        <Link to="/LimitedDropsPage">
          <Button variant="ghost" className="group text-primary hover:text-primary/80">
            View all <ArrowRight className="ml-2 size-4 transition-transform group-hover:translate-x-1" />
          </Button>
        </Link>
      </div>
      
      <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
        {relatedPosts.map((post, i) => (
          <Card key={post.id} data-index={i} className="group overflow-hidden border-border bg-card transition-colors hover:border-primary/50">
            <div className="aspect-[4/3] overflow-hidden bg-muted">
              {post.featuredImage ? (
                <img 
                  src={post.featuredImage} 
                  alt={post.title}
                  className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
              ) : (
                <div className="flex h-full w-full items-center justify-center bg-muted text-muted-foreground">
                  <span className="text-sm">No Image</span>
                </div>
              )}
            </div>
            <CardHeader className="p-6 pb-3">
              <div className="mb-2 flex items-center gap-2 text-xs font-medium text-primary">
                {post.categories?.[0]?.name || "News"}
              </div>
              <Link to={post.link || "#"}>
                <CardTitle className="line-clamp-2 text-xl group-hover:text-primary transition-colors">
                  {post.title}
                </CardTitle>
              </Link>
            </CardHeader>
            <CardContent className="p-6 pt-0">
              <CardDescription className="line-clamp-3 text-muted-foreground">
                {post.excerpt ? (
                  <div dangerouslySetInnerHTML={{ __html: post.excerpt }} />
                ) : (
                  "Read the latest updates on this exclusive release."
                )}
              </CardDescription>
            </CardContent>
            <CardFooter className="p-6 pt-0">
              <Link to={post.link || "#"} className="w-full">
                <Button variant="outline" className="w-full border-border hover:bg-primary hover:text-primary-foreground hover:border-primary">
                  Read Article
                </Button>
              </Link>
            </CardFooter>
          </Card>
        ))}
      </div>
    </div>
  );
}

function RelatedPostsWrapper({ currentPostId }: { currentPostId: string }) {
  
  
  const q = new WP_Query({ 
    per_page: 4,
    orderby: "date",
    order: "desc",
    ignore_sticky: true
  });

  return (
    <PostsProvider wp_query={{ per_page: 4, orderby: "date", order: "desc", ignore_sticky: true }}>
      <RelatedPostsConsumer currentPostId={currentPostId} />
    </PostsProvider>
  );
}



function SinglePostConsumer() {
  const { posts, loading, error, refetch } = usePosts();

  if (loading) {
    return (
      <div className="container mx-auto max-w-4xl px-4 py-20">
        <div className="mb-8 h-12 w-3/4 animate-pulse rounded-lg bg-muted/20" />
        <div className="mb-12 h-96 w-full animate-pulse rounded-xl bg-muted/20" />
        <div className="space-y-4">
          <div className="h-4 w-full animate-pulse rounded bg-muted/20" />
          <div className="h-4 w-full animate-pulse rounded bg-muted/20" />
          <div className="h-4 w-2/3 animate-pulse rounded bg-muted/20" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-[50vh] flex-col items-center justify-center text-center">
        <h2 className="mb-4 text-2xl font-bold text-destructive">Error Loading Post</h2>
        <p className="mb-6 text-muted-foreground">We couldn't load the requested article.</p>
        <Button onClick={() => refetch()}>Try Again</Button>
      </div>
    );
  }

  if (!posts || posts.length === 0) {
    return (
      <div className="flex min-h-[50vh] flex-col items-center justify-center text-center">
        <h2 className="mb-4 text-2xl font-bold">Post Not Found</h2>
        <Link to="/HomePage">
          <Button>Return Home</Button>
        </Link>
      </div>
    );
  }

  const post = posts[0];
  const authorName = post.author?.name || "StrideVault Team";
  const authorAvatar = post.author?.avatar?.["96"] || "";

  return (
    <article className="container mx-auto max-w-5xl px-4 py-12 md:py-20">
      {}
      <header className="mb-12 text-center md:mb-16">
        <div className="mb-6 flex flex-wrap items-center justify-center gap-3 text-sm font-medium text-muted-foreground">
          {post.categories && post.categories.length > 0 && (
            <span className="flex items-center gap-1.5 rounded-full bg-primary/10 px-3 py-1 text-primary">
              <Tag className="size-3.5" />
              {post.categories[0].name}
            </span>
          )}
          <span className="flex items-center gap-1.5">
            <Calendar className="size-3.5" />
            {formatDate(post.date)}
          </span>
          <span className="flex items-center gap-1.5">
            <Clock className="size-3.5" />
            5 min read
          </span>
        </div>

        <h1 className="mb-8 text-4xl font-extrabold leading-tight tracking-tight text-foreground md:text-5xl lg:text-6xl">
          {post.title}
        </h1>

        <div className="flex items-center justify-center gap-4">
          <div className="flex items-center gap-3">
            <Avatar className="size-10 border border-border">
              <AvatarImage src={authorAvatar} alt={authorName} />
              <AvatarFallback className="bg-primary text-primary-foreground">
                {authorName.charAt(0)}
              </AvatarFallback>
            </Avatar>
            <div className="text-left">
              <p className="text-sm font-semibold text-foreground">{authorName}</p>
              <p className="text-xs text-muted-foreground">Author</p>
            </div>
          </div>
        </div>
      </header>

      {}
      {post.featuredImage && (
        <div className="mb-16 overflow-hidden rounded-2xl border border-border bg-muted shadow-2xl">
          <img
            src={post.featuredImage}
            alt={post.title}
            className="h-auto w-full object-cover"
          />
        </div>
      )}

      {}
      <div className="grid grid-cols-1 gap-12 lg:grid-cols-[1fr_300px]">
        {}
        <div className="min-w-0">
          <div 
            className="prose prose-lg prose-invert max-w-none 
              prose-headings:font-bold prose-headings:tracking-tight prose-headings:text-foreground 
              prose-p:leading-relaxed prose-p:text-muted-foreground 
              prose-a:text-primary prose-a:no-underline hover:prose-a:underline 
              prose-strong:text-foreground prose-blockquote:border-l-primary prose-blockquote:bg-muted/30 prose-blockquote:py-2 prose-blockquote:pl-6 prose-blockquote:italic
              prose-img:rounded-xl prose-img:border prose-img:border-border"
            dangerouslySetInnerHTML={{ __html: post.content || "" }}
          />
          
          {}
          <div className="mt-12 flex flex-wrap items-center justify-between gap-4 border-t border-border pt-8">
            <div className="flex flex-wrap gap-2">
              {post.tags?.map((tag) => (
                <span key={tag.id} className="rounded-md border border-border bg-muted/50 px-2.5 py-1 text-xs text-muted-foreground transition-colors hover:border-primary/50 hover:text-foreground">
                  #{tag.name}
                </span>
              ))}
            </div>
            <Button variant="outline" size="sm" className="gap-2">
              <Share2 className="size-4" />
              Share Article
            </Button>
          </div>
        </div>

        {}
        <aside className="space-y-8">
          <div className="sticky top-24 rounded-xl border border-border bg-card p-6 shadow-lg">
            <h3 className="mb-4 text-lg font-bold text-foreground">About the Author</h3>
            <div className="mb-4 flex items-center gap-4">
              <Avatar className="size-14 border border-border">
                <AvatarImage src={authorAvatar} alt={authorName} />
                <AvatarFallback className="bg-primary text-primary-foreground text-lg">
                  {authorName.charAt(0)}
                </AvatarFallback>
              </Avatar>
              <div>
                <p className="font-bold text-foreground">{authorName}</p>
                <p className="text-sm text-muted-foreground">Content Creator</p>
              </div>
            </div>
            <p className="mb-6 text-sm leading-relaxed text-muted-foreground">
              Specializing in performance footwear analysis and sneaker culture trends. Bringing you the latest insights from the world of athletics.
            </p>
            <Link to="/HomePage" className="block">
              <Button variant="outline" className="w-full justify-between group">
                View Profile <ChevronRight className="size-4 text-muted-foreground transition-transform group-hover:translate-x-1" />
              </Button>
            </Link>
          </div>

          {}
          <div className="rounded-xl bg-primary p-6 text-primary-foreground">
            <h3 className="mb-2 text-lg font-bold">Join the Inner Circle</h3>
            <p className="mb-4 text-sm opacity-90">Get exclusive drop alerts and member-only content delivered to your inbox.</p>
            <Button className="w-full bg-background text-foreground hover:bg-background/90 border-0">
              Subscribe Now
            </Button>
          </div>
        </aside>
      </div>

      {}
      <RelatedPostsWrapper currentPostId={post.id} />
    </article>
  );
}



export default function SinglePostTemplateSection() {
  
  
  const currentParams = (typeof window !== 'undefined' && (window as any).wvcClient?.getCurrentWPQueryParams()) || {};
  
  
  
  
  const wpQuery = new WP_Query(currentParams);

  return (
    <section id="single-post-template-section" className="bg-background text-foreground">
      <PostsProvider wp_query={wpQuery}>
        <SinglePostConsumer />
      </PostsProvider>
    </section>
  );
}